import React from 'react';
import { Spinner } from '../../../_shared/Spinner';
import { Pagination } from './Pagination';

export interface PropertyResult {
	id: number;
	title: string;
	permalink: string;
	image: string;
	location: string;
	guests: number;
	bedrooms: number;
	bathrooms: number;
	hostawayId: string;
	basePrice: number;
	currency: string;
	lat?: number;
	lng?: number;
	pets: boolean;
	parking: boolean;
	description?: string;
	// Rendered server-side by ocean_render_property_card() (php/property-card.php)
	// -- see search_properties_ajax() in php/search-support.php. The single
	// source of truth for card markup, shared with ocean/property-list's
	// render.php and MapView.tsx's marker popup, so this component only
	// supplies the outer <article> and injects the rest.
	cardHtml: string;
	}

interface SearchResultsProps {
	results: PropertyResult[];
	isLoading: boolean;
	currentPage: number;
	onPageChange: (page: number) => void;
	}

// Matches the Journal's query loop (perPage: 12, templates/index.html), so
// both listings break onto a new page at the same count.
export const RESULTS_PER_PAGE = 12;

export const SearchResults = ({ results, isLoading, currentPage, onPageChange }: SearchResultsProps) => {
	if (isLoading) {
		return (
			<div className="property-results__list property-results__list--loading">
				<Spinner />
			</div>
			);
		}

	if (results.length === 0) {
		return <div className="property-results__list property-results__list--empty">No properties found matching your criteria.</div>;
		}

	const totalPages = Math.ceil(results.length / RESULTS_PER_PAGE);
	// Clamped rather than trusted: a page number can outlive the result set
	// that justified it (a filter narrowing 45 results to 10 while on page 3),
	// and an out-of-range slice would render an empty grid under a full count.
	const safePage = Math.min(Math.max(currentPage, 1), totalPages);
	const pageStart = (safePage - 1) * RESULTS_PER_PAGE;
	const pageResults = results.slice(pageStart, pageStart + RESULTS_PER_PAGE);

	return (
		<div className="property-results__list">
			<div className="property-results__grid">
				{pageResults.map((property) => (
					<article
						key={property.id}
						// The same identity classes the server puts on a property-list card, so a card
						// found by search is as identifiable as one rendered into a template. Written
						// out here rather than fetched because the post type is fixed for this block
						// and the id is already in the response.
						className={`property-list__card post-${property.id} property type-property status-publish`}
						dangerouslySetInnerHTML={{ __html: property.cardHtml }}
					/>
				))}
			</div>

			<Pagination
				currentPage={safePage}
				totalPages={totalPages}
				onPageChange={onPageChange}
			/>
		</div>
		);
};
